import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios, {
  authHeaders,
  authHeaders2,
  refreshToken,
} from "../../helpers/axios";
import { notify, notifyAPIMessages } from "../../helpers/commonFunction";
import { messages } from "../../helpers/messages";
import { apiEndpoints } from "../../helpers/constants";
import { insertUserDataInDB, updateNewUserSessionInDB } from "./SliceDBQuery";

export const reqToUserLogin = createAsyncThunk(
  apiEndpoints.login,
  async ({ data, onSuccess, onFailure }, { rejectWithValue }) => {
    try {
      const newData = {
        ...data,
        username: data?.username ? data?.username?.trim() : "",
        password: data?.password ? data?.password?.trim() : "",
      };
      const response = await Axios.post(apiEndpoints.login, newData);
      const authToken = response?.headers["authorization"];
      const reqObj = {
        ...response?.data,
        timein_date: null,
        timeout_date: null,
        user_id: response?.data?.id,
        token: authToken,
        password: newData?.password || null,
        app_name: window?.location?.hostname,
        app_type: process.env.REACT_APP_NAME || "RX",
      };
      await insertUserDataInDB(reqObj);
      onSuccess?.(response);
      return response;
    } catch (error) {
      onFailure?.(error);
      return rejectWithValue(error);
    }
  }
);

export const reqToUserLogout = createAsyncThunk(
  apiEndpoints.logout,
  async ({ data, onSuccess, onFailure }, { rejectWithValue }) => {
    const request = {
      method: "POST",
      url: apiEndpoints.logout,
      data,
      headers: authHeaders2(),
    };
    try {
      const response = await Axios(request);
      onSuccess?.(response);
      return response;
    } catch (error) {
      if (error?.response && [401, 403].includes(error?.response?.status)) {
        try {
          const response = await handleTokenRefresh(request);
          onSuccess?.(response);
          return response;
        } catch (error) {
          onFailure?.(error);
          return rejectWithValue(error);
        }
      } else {
        onFailure?.(error);
        return rejectWithValue(error);
      }
    }
  }
);

export const handleTokenRefresh = async (request) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await refreshToken();
      const { token, refresh_token } = response.data;
      localStorage.setItem("token", JSON.stringify(token));
      localStorage.setItem("refresh_token", JSON.stringify(refresh_token));
      await updateNewUserSessionInDB(token, refresh_token);
      const newHeaders = {
        ...request.headers,
        Authorization: `Bearer ${token}`,
      };
      const response2 = await Axios({
        ...request,
        headers: newHeaders,
      });
      resolve(response2);
    } catch (error) {
      reject(error);
    }
  });
};

export const reqToUpdateAccessToken = createAsyncThunk(
  apiEndpoints.getAccessToken,
  async ({ data, onSuccess, onFailure }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        apiEndpoints.getAccessToken,
        data,
        authHeaders()
      );
      onSuccess?.(response.data);
      return response;
    } catch (error) {
      onFailure?.(error);
      return rejectWithValue(error);
    }
  }
);

const initialUser = localStorage.getItem("user")
  ? JSON.parse(localStorage.getItem("user"))
  : null;

const initialState = {
  loader: false,
  user: initialUser,
  error: "",
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    updateUserState: async (state, action) => {
      const userData = { ...action.payload };
      if (userData?.id) {
        state.user = userData;
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("token", JSON.stringify(userData?.token));
        localStorage.setItem(
          "refresh_token",
          JSON.stringify(userData?.refresh_token)
        );
      }
    },
    reqToUserLoginInLocal: (state, action) => {
      const userData = { ...action.payload };
      state.user = userData;
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("token", JSON.stringify(userData?.token));
      localStorage.setItem(
        "refresh_token",
        JSON.stringify(userData?.refresh_token)
      );
      notify(messages.toast.login, "success");
    },
    reqToUserLogoutInLocal: (state) => {
      state.user = null;
      localStorage.removeItem("user");
      notify(messages.toast.logout, "success");
    },
    reqToForceUserLogout: (state) => {
      state.user = null;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
    },
  },
  extraReducers: (builder) => {
    // LOGIN
    builder
      .addCase(reqToUserLogin.pending, (state) => {
        state.loader = true;
      })
      .addCase(reqToUserLogin.fulfilled, (state, action) => {
        state.loader = false;
        if (action?.payload?.status === 200) {
          notify(messages.toast.login, "success");
          const reqObj = {
            ...action.payload.data,
            timein_date: null,
            timeout_date: null,
            user_id: action.payload.data.id,
          };
          state.user = reqObj;
          localStorage.setItem("user", JSON.stringify(reqObj));
          localStorage.setItem("token", JSON.stringify(reqObj?.token));
          localStorage.setItem(
            "refresh_token",
            JSON.stringify(reqObj?.refresh_token)
          );
        }
      })
      .addCase(reqToUserLogin.rejected, (state, action) => {
        state.loader = false;
        state.error = action.payload;
        notifyAPIMessages(action.payload);
      });

    // LOGOUT
    builder
      .addCase(reqToUserLogout.pending, (state) => {
        state.loader = true;
      })
      .addCase(reqToUserLogout.fulfilled, (state, action) => {
        state.loader = false;
        if (action.payload.status === 200) {
          state.user = null;
          localStorage.removeItem("user");
          localStorage.removeItem("token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("locationError");
          notify(messages.toast.logout, "success");
        }
      })
      .addCase(reqToUserLogout.rejected, (state, action) => {
        state.loader = false;
        state.error = action.payload;
        notifyAPIMessages(action.payload);
      });
    // UPDATE ACCESS TOKEN
    builder
      .addCase(reqToUpdateAccessToken.pending, () => {})
      .addCase(reqToUpdateAccessToken.fulfilled, () => {})
      .addCase(reqToUpdateAccessToken.rejected, () => {});
  },
});

export const {
  updateUserState,
  reqToUserLoginInLocal,
  reqToUserLogoutInLocal,
  reqToForceUserLogout,
} = userSlice.actions;
export default userSlice.reducer;
